import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import * as TileLayerModule from 'ol/layer/Tile.js';
const TileLayer = TileLayerModule.default as typeof TileLayerModule.default;
import * as TileWMSModule from 'ol/source/TileWMS.js';
const TileWMS = TileWMSModule.default as typeof TileWMSModule.default;
import XYZ from 'ol/source/XYZ.js';

import { OlAtlasBaseService } from 'src/app/services/ol-map/ol-atlas-base.service';
import { Feature, MapBrowserEvent } from 'ol';
import { Geometry } from 'ol/geom.js';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { OLMapService } from 'src/app/services/ol-map/ol-map.service';
import { TREIN_LAYER_NAME, TREIN_WORKSPACE } from 'src/assets/constants/constants';
import { TreinFormService } from './trein-form.service';
import { trainDetails } from './trein-presentation';

@Injectable({
    providedIn: 'root',
})
export class TreinMapService extends OlAtlasBaseService {
    private restoreBasemaps: Array<() => void> = [];

    softenBasemap(): void {
        this.restoreBasemap();
        this.olMapService.map.getLayers().forEach((layer) => {
            if (layer instanceof TileLayer && layer.getSource() instanceof XYZ) {
                const opacity = layer.getOpacity();
                this.restoreBasemaps.push(() => layer.setOpacity(opacity));
                layer.setOpacity(0.45);
            }
        });
    }

    restoreBasemap(): void {
        this.restoreBasemaps.forEach((restore) => restore());
        this.restoreBasemaps = [];
    }

    releaseLayer(): void {
        this.restoreBasemap();
        this.olMapService.layers
            .filter((entry) => entry.name === TREIN_LAYER_NAME)
            .forEach((entry) => this.olMapService.map?.removeLayer(entry.layer));
        this.olMapService.layers = this.olMapService.layers.filter((entry) => entry.name !== TREIN_LAYER_NAME);
    }
    constructor(
        private http: HttpClient,
        public olMapService: OLMapService,
        public treinFormService: TreinFormService
    ) {
        super(olMapService);
    }

    loadLayer(): void {
        const cqlFilter = Object.keys(this.treinFormService.form.value)
            .filter((key) => this.treinFormService.form.value[key])
            .map((key) => `${key}='${this.treinFormService.form.value[key]}'`)
            .join(' AND ');

        const existingLayer = this.olMapService.layers.find((layer) => layer.name === TREIN_LAYER_NAME);
        if (existingLayer) {
            const source = existingLayer.layer.getSource() as any;
            if (source.getParams().CQL_FILTER === cqlFilter && existingLayer.layer.getVisible()) {
                return;
            }

            this.removeVectorLayers();
            source.updateParams({ CQL_FILTER: cqlFilter });
            existingLayer.layer.setVisible(true);
            return;
        }

        this.removeVectorLayers();
        const tileLayer = new TileLayer({
            preload: 0,
            source: new TileWMS({
                url: `${environment.geoServer.baseUrl}/${TREIN_WORKSPACE}/wms`,
                params: {
                    LAYERS: `${TREIN_WORKSPACE}:${TREIN_LAYER_NAME}`,
                    FORMAT: 'image/png',
                    STYLES: 'treinmetingen_indoor',
                    TILED: true,
                    VERSION: '1.1.1',
                    CQL_FILTER: cqlFilter,
                },
                serverType: 'geoserver',
            }),
        });
        this.olMapService.map.addLayer(tileLayer);
        this.olMapService.layers.push({ name: TREIN_LAYER_NAME, layer: tileLayer });
    }
    updateDetails(feature: Feature<Geometry>) {
        this.olMapService.detailsLoading = true;
        this.getLayerDetails(feature);
        this.olMapService.detailsLoading = false;
    }

    getLayerDetails(feature: Feature<Geometry>): void {
        this.olMapService.details = trainDetails(feature.getProperties());
        this.olMapService.landlinePointDetails = [];
        this.olMapService.detailPanelOpen = true;
        this.olMapService.detailsLoading = false;
    }

    mapOperations(event: MapBrowserEvent<MouseEvent>) {
        return this.createJoinEndpoints(event);
    }

    createJoinEndpoints(event: MapBrowserEvent<MouseEvent>): Observable<Object>[] {
        const tileLayers = event.map
            .getLayers()
            .getArray()
            .filter(
                (l: any) =>
                    l instanceof TileLayer &&
                    l.getSource() instanceof TileWMS &&
                    l.getSource().getParams().LAYERS === `${TREIN_WORKSPACE}:${TREIN_LAYER_NAME}` &&
                    l.getVisible()
            ) as any[];

        if (!tileLayers.length) {
            return [];
        }

        const tileSources = tileLayers.map((tl: any) => tl.getSource()) as any[];
        if (!tileSources.length) {
            return [];
        }

        const viewResolution = event.map.getView().getResolution() ?? 0;

        const urls = tileSources
            .map((ts: any) =>
                ts.getFeatureInfoUrl(event.coordinate, viewResolution, 'EPSG:3857', {
                    INFO_FORMAT: 'application/json',
                    FEATURE_COUNT: 1,
                    BUFFER: 12,
                })
            )
            .filter((url: string | undefined): url is string => !!url);

        return urls.map((url: string) => this.http.get(url));
    }
}
